import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { DataGrid, GridColDef, ptBR } from '@mui/x-data-grid';
import { userAdminService } from '../../services/userAdminService';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CustomPagination from '../../components/CustomPagination';

interface UserAdmin {
  id: number;
  name: string;
  email: string;
  created_date: string;
  updated_date: string;
}

const UserAdminList = () => {
  const [userAdmins, setUserAdmins] = useState<UserAdmin[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [totalRows, setTotalRows] = useState(0);
  const navigate = useNavigate();
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [deletingName, setDeletingName] = useState<string>('');
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchUserAdmins = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await userAdminService.listUserAdmins(
        paginationModel.page + 1,
        paginationModel.pageSize
      );
      
      setUserAdmins(response.content);
      setTotalRows(response.total);
    } catch (err) {
      setError('Erro ao carregar administradores. Tente novamente mais tarde.');
      console.error('Error fetching user admins:', err);
    } finally {
      setLoading(false);
    }
  }, [paginationModel.page, paginationModel.pageSize]);

  useEffect(() => {
    fetchUserAdmins();
  }, [fetchUserAdmins]);

  const handleDeleteClick = (id: number, name: string) => {
    setDeletingId(id);
    setDeletingName(name);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingId) return;

    try {
      setIsDeleting(true);
      await userAdminService.deleteUserAdmin(deletingId);
      await fetchUserAdmins();
    } catch (err) {
      setError('Erro ao deletar administrador. Tente novamente mais tarde.');
      console.error('Error deleting user admin:', err);
    } finally {
      setIsDeleting(false);
      setDeletingId(null);
      setDeletingName('');
    }
  };

  const columns: GridColDef[] = [
    { 
      field: 'id', 
      headerName: 'ID', 
      width: 90 
    },
    { 
      field: 'name', 
      headerName: 'Nome', 
      flex: 1,
      minWidth: 200,
    },
    { 
      field: 'email', 
      headerName: 'E-mail', 
      flex: 1,
      minWidth: 200,
    },
    { 
      field: 'created_date', 
      headerName: 'Data de Cadastro', 
      flex: 1,
      minWidth: 180,
      valueFormatter: (params) => {
        return format(new Date(params.value), 'dd/MM/yyyy HH:mm');
      },
    },
    { 
      field: 'updated_date', 
      headerName: 'Última Atualização', 
      flex: 1,
      minWidth: 180,
      valueFormatter: (params) => {
        return format(new Date(params.value), 'dd/MM/yyyy HH:mm');
      },
    },
    {
      field: 'actions',
      headerName: 'Ações',
      width: 120,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Editar">
            <IconButton
              onClick={() => navigate(`/user-admins/${params.row.id}/edit`)}
              size="small"
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Deletar">
            <IconButton
              onClick={() => handleDeleteClick(params.row.id, params.row.name)}
              size="small"
              color="error"
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        mb: 3
      }}>
        <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
          Administradores
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/user-admins/create')}
        >
          Novo Administrador
        </Button>
      </Box>

      <Paper sx={{ p: { xs: 2, sm: 3 }, mt: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ height: 600, width: '100%' }}>
          <DataGrid
            rows={userAdmins}
            columns={columns}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 25, 50]}
            rowCount={totalRows}
            loading={loading}
            paginationMode="server"
            localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
            disableRowSelectionOnClick
            slots={{
              pagination: CustomPagination,
            }}
            sx={{
              '& .MuiDataGrid-cell:focus': {
                outline: 'none',
              },
            }}
          />
        </Box>
      </Paper>

      <Dialog
        open={deletingId !== null}
        onClose={() => {
          setDeletingId(null);
          setDeletingName('');
        }}
      >
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          Tem certeza que deseja excluir o administrador "{deletingName}"?
          Esta ação não pode ser desfeita.
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeletingId(null);
              setDeletingName('');
            }}
            disabled={isDeleting}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={20} /> : null}
          >
            {isDeleting ? 'Deletando...' : 'Deletar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserAdminList; 